import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { QuoteButton, CallButton } from "./Cta";
import { NAV, BUSINESS } from "@/content/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-[#08090a]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between gap-6 px-5 sm:px-8 lg:px-12">
        <Logo />
        <nav className="hidden xl:flex items-center gap-7" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.id} to={n.to} end data-testid={`nav-link-${n.id}`}
              className={({ isActive }) => `group relative text-[13px] font-medium transition-colors ${isActive ? "text-white" : "text-zinc-300 hover:text-white"}`}>
              {({ isActive }) => (
                <>
                  {n.label}
                  <span className={`absolute -bottom-1.5 left-0 h-px bg-amber-400 transition-[width] duration-500 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <CallButton testId="nav-call-btn" className="hidden md:inline-flex !px-5 !py-3">Call Now</CallButton>
          <QuoteButton testId="nav-quote-btn" className="hidden sm:inline-flex !px-5 !py-3">Get a Free Quote</QuoteButton>
          <a href={BUSINESS.phoneHref} aria-label="Call now" data-testid="nav-mobile-call-icon"
            className="sm:hidden grid h-11 w-11 place-items-center rounded-full bg-amber-400 text-black">
            <Phone className="h-4.5 w-4.5" />
          </a>
          <button type="button" aria-label={open ? "Close menu" : "Open menu"} onClick={() => setOpen((o) => !o)}
            data-testid="nav-menu-toggle" className="xl:hidden grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-nav" data-testid="mobile-nav" aria-label="Mobile"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden overflow-hidden border-t border-white/10 bg-[#08090a]/95"
          >
            <ul className="px-5 sm:px-8 py-6 space-y-1">
              {NAV.map((n, i) => (
                <motion.li key={n.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink to={n.to} end data-testid={`mobile-nav-link-${n.id}`}
                    className={({ isActive }) => `flex items-center justify-between border-b border-white/5 py-3.5 font-display text-lg ${isActive ? "text-amber-400" : "text-white"}`}>
                    {n.label}
                    <span className="font-mono text-xs text-zinc-500">0{i + 1}</span>
                  </NavLink>
                </motion.li>
              ))}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
